import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  Timer, Clock, Loader2, RefreshCw, Plus, Trash2, Calendar, Send
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date - now;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 0) return 'Overdue';
  if (diffMins < 60) return `In ${diffMins} mins`;
  if (diffHours < 24) return `In ${diffHours} hours`;
  return `In ${diffDays} days`;
};

const ScheduledPostsManager = ({ groupId }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    text_body: '',
    scheduled_time: '',
    media_urls: []
  });

  const fetchPosts = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await api.get(`/groups/${groupId}/scheduled-posts`);
      setPosts(res.data.posts || []);
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [groupId]);

  const handleCreate = async () => {
    if (!formData.text_body.trim() || !formData.scheduled_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      await api.post(`/groups/${groupId}/scheduled-posts`, {
        text_body: formData.text_body,
        scheduled_time: new Date(formData.scheduled_time).toISOString(),
        media_urls: formData.media_urls
      });
      toast.success('Post scheduled successfully');
      setShowCreateDialog(false);
      setFormData({ text_body: '', scheduled_time: '', media_urls: [] });
      fetchPosts(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule post');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!confirm('Delete this scheduled post?')) return;
    try {
      await api.delete(`/groups/${groupId}/scheduled-posts/${postId}`);
      toast.success('Scheduled post deleted');
      setPosts(posts.filter(p => p.scheduled_post_id !== postId));
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Scheduled Posts
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchPosts(true)}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
            <Button 
              size="sm" 
              onClick={() => setShowCreateDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Schedule Post
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Timer className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No scheduled posts</p>
              <p className="text-sm">Schedule posts to publish them later</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-4">
                {posts.map((post) => (
                  <div 
                    key={post.scheduled_post_id} 
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-blue-100 text-blue-700">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatRelativeTime(post.scheduled_time)}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-800 line-clamp-3 mb-2">
                          {post.text_body}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Scheduled for {formatDateTime(post.scheduled_time)}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Created by {post.user_name}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(post.scheduled_post_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule a Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="post-content">Post Content *</Label>
              <Textarea
                id="post-content"
                placeholder="What do you want to share?"
                value={formData.text_body}
                onChange={(e) => setFormData(prev => ({ ...prev, text_body: e.target.value }))}
                className="mt-1"
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="schedule-time">Schedule Time *</Label>
              <Input
                id="schedule-time"
                type="datetime-local"
                value={formData.scheduled_time}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                className="mt-1"
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate}
              disabled={creating || !formData.text_body || !formData.scheduled_time}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScheduledPostsManager;
